import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { Checkbox, Flex, Text } from "@radix-ui/themes";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { SpaceAccountsSearchForm } from "@/pages/space-accounts/root/space-accounts-search-form";
import { SpaceAccountsTable } from "@/pages/space-accounts/root/space-accounts-table";
import { SpaceAccountSummarySchema } from "@/domains/openapi/types";

export const SpaceAccountsRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL query parameters
  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const [searchTerm, setSearchTerm] = useState<string>(initialQuery);
  const [showSoftDeleted, setShowSoftDeleted] = useState<boolean>(false);

  // Update searchTerm when URL changes
  useEffect(() => {
    const query = searchParams.get("query") ?? "";
    setSearchTerm(query);
  }, [searchParams]);

  // React Query to perform the search based on searchTerm
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["space-accounts-search", searchTerm],
    queryFn: () =>
      api.post("/internal/v1/space-accounts/search", {
        body: {
          query_string: searchTerm,
          limit: pageSize,
          only_primary_space_accounts: true,
        },
      }),
    enabled: searchTerm.length > 0,
    // 5 minutes
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleViewSpaceAccount = (id: string) => {
    navigate(atlasRoutes.spaceAccountsView({ params: { spaceAccountId: id } }).path);
  };

  const handleSearch = (query: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (query) nextParams.set("query", query);
    else nextParams.delete("query");
    setSearchParams(nextParams);
  };

  // Prepare the data for the table
  const tableData = useMemo<SpaceAccountSummarySchema[]>(() => data?.data?.results ?? [], [data]);
  const filteredTableData = useMemo<SpaceAccountSummarySchema[]>(
    () => (showSoftDeleted ? tableData : tableData.filter(x => x.is_soft_deleted !== true)),
    [showSoftDeleted, tableData]
  );

  return (
    <Container>
      <h1>Space Accounts</h1>
      <DescriptionText>Search by name or email address.</DescriptionText>
      <SubText>
        <i>Note that this list only shows "primary" space accounts.</i>
      </SubText>
      <br />
      {/* Search Form */}
      <SpaceAccountsSearchForm initialValue={searchTerm} onSearch={handleSearch} />
      <FilterRow align="center" gap="2">
        <Checkbox
          id="show-soft-deleted-space-accounts"
          checked={showSoftDeleted}
          onCheckedChange={value => setShowSoftDeleted(value === true)}
        />
        <Text as="label" htmlFor="show-soft-deleted-space-accounts">
          Show soft deleted
        </Text>
      </FilterRow>
      {/* Loading and Error States */}
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching accounts: {error.message}</div>}
      {/* Table or No Results */}
      {filteredTableData.length > 0 ? (
        <SpaceAccountsTable
          data={filteredTableData}
          onViewSpaceAccount={handleViewSpaceAccount}
          pageSize={pageSize}
        />
      ) : (
        !isLoading && searchTerm && <div>No space accounts found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;

const SubText = styled(Text)`
  display: block;
  font-size: 10px;
  color: var(--gray-11);
`;

const FilterRow = styled(Flex)`
  margin-top: -12px;
  margin-bottom: 16px;
`;
